import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, finalize, retry } from 'rxjs/operators';
import { CoreConfig } from '../utils/config';

/**
 * Api service
 */
@Injectable({
  providedIn: 'root',
})
export class ApiService {
  /**
   * Options for api calls
   *
   * @private
   */
  private options = {
    headers: new HttpHeaders().set('Content-Type', 'application/json'),
  };

  /**
   * Api url
   *
   * @private
   */
  private API_URL: string;

  /**
   * Loading subject
   *
   * @private
   */
  private loadingSubject = new BehaviorSubject<boolean>(false);

  /**
   *
   */
  public get loading$() {
    return this.loadingSubject.asObservable();
  }

  /**
   * Constructor
   *
   * @param httpClient HttpClient
   * @param snackBar MatSnackBar
   */
  constructor(@Inject(CoreConfig.API_BASE_URL) public baseUrl: string, private httpClient: HttpClient, private snackBar: MatSnackBar) {
    this.API_URL = baseUrl;
  }

  /**
   * Get
   *
   * @param path Path
   * @param params HttpParams
   *
   * @returns Observable
   */
  public get(path: string): Observable<any> {
    return this.httpClient.get(this.API_URL + path, this.options).pipe(
      retry(2),
      catchError((err) => this.formatErrors(err)),
      finalize(() => this.loadingSubject.next(false))
    );
  }

  /**
   * Put
   *
   * @param path Path
   * @param body Body
   *
   * @returns Observable
   */
  public put(path: string, body: object = {}): Observable<any> {
    return this.httpClient.put(this.API_URL + path, JSON.stringify(body), this.options).pipe(
      catchError((err) => this.formatErrors(err)),
      finalize(() => this.loadingSubject.next(false))
    );
  }

  /**
   * Patch
   *
   * @param path Path
   * @param body Body
   *
   * @returns Observable
   */
  public patch(path: string, body: object = {}): Observable<any> {
    return this.httpClient.patch(this.API_URL + path, JSON.stringify(body), this.options).pipe(
      catchError((err) => this.formatErrors(err)),
      finalize(() => this.loadingSubject.next(false))
    );
  }

  /**
   * Post
   *
   * @param path Path
   * @param body Body
   *
   * @returns Observable
   */
  public post(path: string, body: object = {}): Observable<any> {
    this.loadingSubject.next(true);

    return this.httpClient.post(this.API_URL + path, JSON.stringify(body), this.options).pipe(
      catchError((err) => this.formatErrors(err)),
      finalize(() => this.loadingSubject.next(false))
    );
  }

  /**
   * Delete
   *
   * @param path Path
   *
   * @returns Observable
   */
  public delete(path: string): Observable<any> {
    return this.httpClient.delete(this.API_URL + path, this.options).pipe(
      catchError((err) => this.formatErrors(err)),
      finalize(() => this.loadingSubject.next(false))
    );
  }

  /**
   * Format and get errors
   *
   * @param error Error
   *
   * @returns Observable
   */
  public formatErrors(error: HttpErrorResponse): Observable<any> {
    this.snackBar.open(error.error.message, 'Okey', {
      duration: 15000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
    });

    return throwError(error.error);
  }
}
